import { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { setTheme } from '../store/slices/uiSlice';

const THEME_STORAGE_KEY = 'vangard-theme';

type Theme = 'light' | 'dark';

/**
 * Хук для управления тёмной/светлой темой.
 * - Читает сохранённую тему из localStorage
 * - Применяет data-theme атрибут на document.documentElement
 * - Синхронизирует с Redux store
 */
export function useTheme() {
  const dispatch = useDispatch();
  const currentTheme = useSelector((state: RootState) => state.ui.theme);

  // Применяем тему к DOM
  const applyTheme = useCallback((theme: Theme) => {
    document.documentElement.setAttribute('data-theme', theme);
    // Для meta theme-color (мобильные браузеры)
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', theme === 'dark' ? '#0f1117' : '#f8fafc');
    }
  }, []);

  // Инициализация при монтировании
  useEffect(() => {
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
    const theme = savedTheme || 'light';
    
    dispatch(setTheme(theme));
    applyTheme(theme);
  }, [dispatch, applyTheme]);

  // Синхронизация при изменении темы в store
  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme, applyTheme]);

  // Переключение темы
  const toggleTheme = useCallback(() => {
    const newTheme: Theme = currentTheme === 'light' ? 'dark' : 'light';
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    dispatch(setTheme(newTheme));
    applyTheme(newTheme);
  }, [currentTheme, dispatch, applyTheme]);

  return {
    theme: currentTheme,
    toggleTheme,
    isDark: currentTheme === 'dark',
  };
}
